import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from 'react-final-form'
import type { ScheduleFormData } from '../_utils/types'
import { Button } from '@/components/ui/button'
import { T, useTranslate } from '@tolgee/react'
import {
  ScheduleFormFieldAgent,
  ScheduleFormFieldEndDate,
  ScheduleFormFieldFrequency,
  ScheduleFormFieldParameters,
  ScheduleFormFieldStartDate,
  ScheduleFormFieldStartTime,
} from './form-fields'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordian'
import { useAgentRegistryQuery } from '@/hooks/api/use-agent-registry-query'
import React from 'react'

type ScheduleBaseDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  triggerNode?: React.ReactNode
  handleSubmit: (values: ScheduleFormData) => Promise<void>
  isSubmitting: boolean
  title: string
  actionTitle: string
  isEditMode?: boolean
  initialValues: ScheduleFormData
}

export function ScheduleBaseDialog(props: ScheduleBaseDialogProps) {
  const agentRegistryQuery = useAgentRegistryQuery()
  const { open, onOpenChange, handleSubmit, isSubmitting, title, actionTitle, initialValues } =
    props
  const { t } = useTranslate()
  const filteredAgentsData = React.useMemo(() => {
    return agentRegistryQuery.data?.data?.filter((agent) => agent.type === 'workflow')
  }, [agentRegistryQuery.data?.data])

  const validateForm = (values: ScheduleFormData) => {
    const errors: { [key in keyof ScheduleFormData]?: boolean } = {}

    if (!values.agentId) {
      errors.agentId = true
    }

    if (!values.startDate) {
      errors.startDate = true
    }

    if (!values.startTime) {
      errors.startTime = true
    }

    if (!values.frequency) {
      errors.frequency = true
    }

    return errors
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {props?.triggerNode && <DialogTrigger asChild>{props?.triggerNode}</DialogTrigger>}
      <DialogPortal>
        <DialogContent className='sm:max-w-2xl'>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <DialogDescription className='hidden'>{title}</DialogDescription>
          <Form<ScheduleFormData>
            onSubmit={handleSubmit}
            validate={validateForm}
            initialValues={initialValues}
            render={({ handleSubmit: formHandleSubmit, submitting, invalid, values, form }) => {
              const selectedAgent = React.useMemo(() => {
                return filteredAgentsData?.find((agent) => agent.id === values.agentId)
              }, [filteredAgentsData, values?.agentId])

              const selectedAgentParameters = React.useMemo(() => {
                return selectedAgent?.tools?.[0]?.input
              }, [selectedAgent?.tools?.[0]?.input])

              React.useEffect(() => {
                if (values?.agentId) {
                  form.reset({ ...initialValues, agentId: values?.agentId })
                }
              }, [values?.agentId])
              return (
                <>
                  <div className='flex flex-col gap-6'>
                    <ScheduleFormFieldAgent
                      disabled={!!initialValues?.agentId || isSubmitting}
                      agentsData={filteredAgentsData || []}
                    />

                    {values?.agentId && (
                      <>
                        <Accordion type='single' collapsible className='w-full'>
                          <AccordionItem value='description'>
                            <AccordionTrigger className='h-auto justify-start p-0 font-circular-xx font-light'>
                              <T keyName='schedule.addButton.fields.agentDescription' />
                            </AccordionTrigger>
                            <AccordionContent className='mt-2 py-0'>
                              <p className='rounded border border-neutral-75 bg-neutral-50 p-2 font-circular-xx text-sm font-light'>
                                {selectedAgent?.description ||
                                  t('schedule.dialogs.placeholders.agentDescription')}
                              </p>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>

                        {!!selectedAgentParameters?.length && (
                          <ScheduleFormFieldParameters
                            disabled={isSubmitting}
                            selectedAgentParameters={selectedAgentParameters}
                          />
                        )}

                        <div className='flex flex-col gap-2'>
                          <p className='font-light'>
                            <T keyName='schedule.addButton.section.schedule' />
                          </p>
                          <div className='grid grid-cols-2 gap-4'>
                            <ScheduleFormFieldStartDate disabled={isSubmitting} />
                            <ScheduleFormFieldEndDate disabled={isSubmitting} />
                            <ScheduleFormFieldStartTime disabled={false} />
                            <ScheduleFormFieldFrequency disabled={false} />
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <DialogFooter className='flex-shrink-0 gap-2'>
                    <DialogClose asChild>
                      <Button
                        variant={'mango-outline'}
                        title={t('shared.cancelDialog.a11y')}
                        disabled={submitting}
                        type='button'
                      >
                        <T keyName='schedule.addButton.buttons.cancel' />
                      </Button>
                    </DialogClose>
                    <Button
                      onClick={formHandleSubmit}
                      type='submit'
                      loading={submitting}
                      title={t('schedule.addButton.buttons.save')}
                      disabled={invalid || submitting}
                    >
                      {actionTitle}
                    </Button>
                  </DialogFooter>
                </>
              )
            }}
          />
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}
